// src/CustomerSurveyList.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { collection, onSnapshot } from "firebase/firestore";
import {
  PROJECT_TYPES,
  ART_STYLES,
  COLORS,
  PAINT_TYPES,
  BOARD_TYPES,
  FRAME_OPTIONS,
} from "./customerSurveyOptions";

const labelFrom = (list, key) => list.find((x) => x.key === key)?.label || "—";

const CustomerSurveyList = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "customerSurveys"), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );
      setSurveys(list);
    });
    return () => unsub();
  }, []);

  const filtered = surveys.filter((s) =>
    (s.customerInfo?.name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const formatDate = (ts) =>
    ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString("ar-IQ") : "—";

  return (
    <Container sx={{ mt: 4, mb: 6 }} dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={900}>
          📋 استبيانات الزبائن
        </Typography>
        <Chip
          label="+ استبيان جديد"
          color="primary"
          onClick={() => navigate("/customer-survey")}
          sx={{ cursor: "pointer", fontWeight: "bold" }}
        />
      </Box>

      <TextField
        fullWidth
        sx={{ mb: 3 }}
        label="🔍 ابحث باسم الزبون"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filtered.length === 0 && (
        <Typography color="text.secondary">لا توجد استبيانات بعد</Typography>
      )}

      {filtered.map((s) => (
        <Paper
          key={s.id}
          sx={{
            p: 2,
            mb: 1.5,
            borderRadius: 2,
            cursor: "pointer",
            "&:hover": { boxShadow: 3 },
          }}
          onClick={() => setSelected(s)}
        >
          <Typography fontWeight="bold">
            👤 {s.customerInfo?.name || "—"} — {s.customerInfo?.phone || "—"}
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            📍 {s.customerInfo?.governorate || "—"} | 🕒 {formatDate(s.createdAt)}
          </Typography>
        </Paper>
      ))}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          تفاصيل الاستبيان
          <IconButton
            onClick={() => setSelected(null)}
            sx={{ position: "absolute", left: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {selected && (
          <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography fontWeight="bold">
              👤 {selected.customerInfo?.name} — {selected.customerInfo?.phone}
            </Typography>
            <Typography>📍 {selected.customerInfo?.governorate}</Typography>
            <Typography>
              🏠 {PROJECT_TYPES[selected.customerInfo?.projectType]?.label || "—"} — {selected.customerInfo?.subtype}
            </Typography>

            <Typography sx={{ mt: 1 }}>
              💡 الفكرة: {selected.q1?.ideaText || "—"}
            </Typography>
            {selected.q1?.ideaImages?.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selected.q1.ideaImages.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={`idea-${i}`} style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 6 }} />
                  </a>
                ))}
              </Box>
            )}

            <Typography>
              📍 مكان التعليق: {selected.q2?.location === "مكان آخر" ? selected.q2?.otherText : selected.q2?.location}
            </Typography>

            {selected.q3?.wallImages?.length > 0 && (
              <>
                <Typography>🖼️ صور الجدار:</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {selected.q3.wallImages.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt={`wall-${i}`} style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 6 }} />
                    </a>
                  ))}
                </Box>
              </>
            )}

            <Typography>✨ الشعور: {(selected.q4?.feelings || []).join("، ") || "—"}</Typography>
            <Typography>
              🖌️ الأسلوب الفني: {(selected.q5?.styles || []).map((k) => labelFrom(ART_STYLES, k)).join("، ") || "—"}
            </Typography>
            <Typography>
              🎨 الألوان: {(selected.q6?.colors || []).map((k) => labelFrom(COLORS, k)).join("، ") || "—"}
            </Typography>
            {selected.q6?.notes && <Typography>📝 {selected.q6.notes}</Typography>}
            <Typography>🧩 أريد وجود: {(selected.q7?.wanted || []).join("، ") || "—"}</Typography>
            <Typography>🚫 لا أريد وجود: {(selected.q7?.unwanted || []).join("، ") || "—"}</Typography>
            {selected.q7?.notes && <Typography>📝 {selected.q7.notes}</Typography>}
            <Typography>🖌️ نوع الرسم: {labelFrom(PAINT_TYPES, selected.q8?.paintType)}</Typography>
            <Typography>🖼️ نوع اللوحة: {labelFrom(BOARD_TYPES, selected.q9?.boardType)}</Typography>
            <Typography>🖼️ الإطار: {labelFrom(FRAME_OPTIONS, selected.q10?.frameChoice)}</Typography>
            <Typography fontSize={12} color="text.secondary" sx={{ mt: 1 }}>
              أُنشئ بواسطة {selected.createdBy} — {formatDate(selected.createdAt)}
            </Typography>
          </DialogContent>
        )}
      </Dialog>
    </Container>
  );
};

export default CustomerSurveyList;
